import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, Loader2, Mail, KeyRound, ArrowLeft, Check } from 'lucide-react';
import { SENDAFooter } from '../components/SENDAFooter';

export default function RecuperarPassword() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();
    
    // Forgot password state
    const [email, setEmail] = useState('');
    const [emailSent, setEmailSent] = useState(false);
    
    // Reset password state
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [resetSuccess, setResetSuccess] = useState(false);
    
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { forgotPassword, resetPassword } = useAuth();

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const result = await forgotPassword(email);
        setLoading(false);

        if (result.success) {
            setEmailSent(true);
        } else {
            setError(result.error);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError('');

        if (newPassword.length < 8) {
            setError('La contraseña debe tener al menos 8 caracteres');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Las contraseñas no coinciden');
            return;
        }

        setLoading(true);
        const result = await resetPassword(token, newPassword);
        setLoading(false);

        if (result.success) {
            setResetSuccess(true);
            setTimeout(() => navigate('/login'), 3000);
        } else {
            setError(result.error);
        }
    };

    // Reset password form (when token is present)
    if (token) {
        if (resetSuccess) {
            return (
                <div className="min-h-screen bg-[#FBFBF9] flex items-center justify-center p-4">
                    <Card className="w-full max-w-md senda-card animate-fade-in-up">
                        <CardContent className="pt-8 text-center">
                            <div className="mx-auto w-16 h-16 bg-[#7F9C8B] rounded-full flex items-center justify-center mb-4">
                                <Check className="h-8 w-8 text-white" />
                            </div>
                            <h2 className="senda-heading-3 mb-2">Contraseña actualizada</h2>
                            <p className="senda-body mb-4">Tu contraseña ha sido restablecida exitosamente.</p>
                            <p className="senda-small">Redirigiendo al inicio de sesión...</p>
                        </CardContent>
                    </Card>
                </div>
            );
        }

        return (
            <div className="min-h-screen bg-[#FBFBF9] flex items-center justify-center p-4">
                <Card className="w-full max-w-md senda-card animate-fade-in-up">
                    <CardHeader className="space-y-4 text-center pb-8">
                        <div className="mx-auto w-16 h-16 bg-[#A29DB0] rounded-2xl flex items-center justify-center mb-2">
                            <KeyRound className="h-8 w-8 text-white" />
                        </div>
                        <CardTitle className="senda-heading-2">Nueva contraseña</CardTitle>
                        <CardDescription className="senda-body">
                            Ingresa tu nueva contraseña para acceder a tu cuenta.
                        </CardDescription>
                    </CardHeader>
                    
                    <CardContent>
                        <form onSubmit={handleResetPassword} className="space-y-6">
                            {error && (
                                <div className="flex items-center gap-2 p-4 bg-[#D07F7E]/10 border border-[#D07F7E]/20 rounded-lg text-[#D07F7E]" data-testid="reset-password-error">
                                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                                    <span className="text-sm">{error}</span>
                                </div>
                            )}
                            
                            <div className="space-y-2">
                                <Label htmlFor="newPassword" className="senda-label">Nueva contraseña</Label>
                                <Input
                                    id="newPassword"
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="senda-input"
                                    required
                                    data-testid="reset-new-password-input"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword" className="senda-label">Confirmar contraseña</Label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="senda-input"
                                    required
                                    data-testid="reset-confirm-password-input"
                                />
                            </div>
                            
                            <Button
                                type="submit"
                                className="w-full senda-btn-primary"
                                disabled={loading}
                                data-testid="reset-password-submit-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Actualizando...
                                    </>
                                ) : (
                                    'Restablecer contraseña'
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Email sent confirmation
    if (emailSent) {
        return (
            <div className="min-h-screen bg-[#FBFBF9] flex items-center justify-center p-4">
                <Card className="w-full max-w-md senda-card animate-fade-in-up">
                    <CardContent className="pt-8 text-center">
                        <div className="mx-auto w-16 h-16 bg-[#7F9C8B] rounded-full flex items-center justify-center mb-4">
                            <Mail className="h-8 w-8 text-white" />
                        </div>
                        <h2 className="senda-heading-3 mb-2">Revisa tu correo</h2>
                        <p className="senda-body mb-6">
                            Si el correo está registrado, recibirás instrucciones para restablecer tu contraseña.
                        </p>
                        <Link to="/login" className="senda-btn-secondary inline-flex items-center gap-2" data-testid="back-to-login-link">
                            <ArrowLeft className="h-4 w-4" />
                            Volver al inicio de sesión
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Forgot password form
    return (
        <div className="min-h-screen bg-[#FBFBF9] flex items-center justify-center p-4">
            <Card className="w-full max-w-md senda-card animate-fade-in-up">
                <CardHeader className="space-y-4 text-center pb-8">
                    <div className="mx-auto w-16 h-16 bg-[#A29DB0] rounded-2xl flex items-center justify-center mb-2">
                        <Mail className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="senda-heading-2">Recuperar contraseña</CardTitle>
                    <CardDescription className="senda-body">
                        Ingresa tu correo electrónico y te enviaremos instrucciones para restablecer tu contraseña.
                    </CardDescription>
                </CardHeader>
                
                <CardContent>
                    <form onSubmit={handleForgotPassword} className="space-y-6">
                        {error && (
                            <div className="flex items-center gap-2 p-4 bg-[#D07F7E]/10 border border-[#D07F7E]/20 rounded-lg text-[#D07F7E]" data-testid="forgot-password-error">
                                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                                <span className="text-sm">{error}</span>
                            </div>
                        )}
                        
                        <div className="space-y-2">
                            <Label htmlFor="email" className="senda-label">Correo electrónico</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="tu@email.com"
                                className="senda-input"
                                required
                                data-testid="forgot-password-email-input"
                            />
                        </div>
                        
                        <Button
                            type="submit"
                            className="w-full senda-btn-primary"
                            disabled={loading}
                            data-testid="forgot-password-submit-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Enviando...
                                </>
                            ) : (
                                'Enviar instrucciones'
                            )}
                        </Button>
                        
                        <div className="text-center">
                            <Link 
                                to="/login" 
                                className="inline-flex items-center gap-2 text-sm text-[#425A65] hover:text-[#344851] transition-colors"
                                data-testid="back-to-login-from-forgot"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Volver al inicio de sesión
                            </Link>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
